// The Capability pages' design system: the tokens and building blocks the
// Capability Setup page established, for the screens built on top of it (the
// Run Assessment / Publish Training flow first). Dark surface cards with a
// hairline frame, capitals for section titles, brandy for what is selected or
// next, green for what is done, and one bottom bar that carries the step's
// status and its primary action.

import React, { useState } from 'react';
import { Box, CircularProgress, InputAdornment, Menu, MenuItem, Typography } from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { DesktopDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DosenTooltip from '../../../../components/ui/DosenTooltip';
import DosenButton from '../../../../components/ui/Buttons/DosenButton';
import InfoTip from '../../../../components/ui/InfoTip';
import { MOBILE_SX } from '../../../../mobile/mobileApp';

export const FONT = 'Inter, sans-serif';
export const BRANDY = '#F79420';
export const DONE = '#00972D';
export const DANGER = '#FF6A55';
export const TEXT = '#FDFDFD';
export const TEXT_2 = '#B6B6B6';
export const TEXT_3 = '#868686';
export const TEXT_4 = '#565656';
export const LINE = '#2A2A2A';
export const LINE_2 = '#3A3A3A';
export const SURFACE = '#1C1C1C';
export const WELL = '#121212';
export const BRANDY_FRAME = '1px solid rgba(247, 148, 32, 0.45)';
export const BRANDY_TINT = 'rgba(247, 148, 32, 0.06)';
export const BRANDY_FILL = 'rgba(247, 148, 32, 0.12)';

export const LABEL = {
  fontFamily: FONT,
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: '0.08em',
  lineHeight: 1,
  textTransform: 'uppercase' as const,
  color: TEXT_3
};
export const BODY = { fontFamily: FONT, fontSize: 13, lineHeight: 1.55, color: TEXT_2 };
export const SECTION_TITLE = {
  flexShrink: 0,
  fontFamily: FONT,
  fontSize: 14,
  fontWeight: 600,
  color: TEXT,
  whiteSpace: 'nowrap' as const
};

/** A thin scrollbar for a region that scrolls inside a card or a page. */
export const SCROLL = {
  overflowY: 'auto' as const,
  scrollbarWidth: 'thin' as const,
  scrollbarColor: `${TEXT_4} transparent`,
  '&::-webkit-scrollbar': { width: '8px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': { background: TEXT_4, borderRadius: '4px' }
};

/** A section: a surface card with its title on a ruled header (and a note on the right), then its content. */
export const Card: React.FC<{
  title: string;
  /** A short note on the right of the header ("Optional", "2 Selected"). */
  aside?: React.ReactNode;
  children: React.ReactNode;
  sx?: object;
}> = ({ title, aside, children, sx }) => (
  <Box
    component="section"
    sx={{
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      bgcolor: SURFACE,
      border: `1px solid ${LINE}`,
      borderRadius: '8px',
      ...sx
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        minHeight: 46,
        px: '16px',
        borderBottom: `1px solid ${LINE}`
      }}
    >
      <Typography sx={SECTION_TITLE}>{title}</Typography>
      <Box sx={{ flex: 1 }} />
      {typeof aside === 'string' ? (
        <Typography sx={{ fontFamily: FONT, fontSize: 11, fontWeight: 600, color: TEXT_3, whiteSpace: 'nowrap' }}>
          {aside}
        </Typography>
      ) : (
        aside
      )}
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', p: '16px' }}>{children}</Box>
  </Box>
);

/** One input within a section: its label (a brandy mark when required), a line of help, the control. */
export const Field: React.FC<{
  label: string;
  required?: boolean;
  help?: string;
  /** On the right of the label line (a count, a toggle). */
  aside?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, required = false, help, aside, children }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minHeight: 16 }}>
      <Typography sx={{ ...LABEL, color: TEXT_2 }}>
        {label}
        {required && (
          <Box component="span" aria-label="required" sx={{ color: BRANDY, ml: '4px' }}>
            *
          </Box>
        )}
      </Typography>
      <Box sx={{ flex: 1 }} />
      {aside}
    </Box>
    {help && (
      <Typography sx={{ ...BODY, fontSize: 12, lineHeight: 1.45, color: TEXT_3, mt: '-2px' }}>{help}</Typography>
    )}
    {children}
  </Box>
);

export const INPUT_SX = {
  width: '100%',
  boxSizing: 'border-box' as const,
  height: 36,
  px: '12px',
  border: `1px solid ${LINE_2}`,
  borderRadius: '4px',
  bgcolor: WELL,
  color: TEXT,
  fontFamily: FONT,
  fontSize: 13,
  outline: 'none',
  colorScheme: 'dark',
  transition: 'border-color 0.15s',
  '&::placeholder': { color: TEXT_4 },
  '&:hover': { borderColor: TEXT_3 },
  '&:focus': { borderColor: BRANDY }
};

/**
 * The page's button, in the house style (DosenButton): the orange primary for
 * the next thing to do, the dark secondary otherwise. Text only. Disabled with
 * a reason shows the reason on hover.
 */
export const ActionButton: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  primary?: boolean;
  disabled?: boolean;
  /** Why it can't be pressed yet. */
  reason?: string;
  type?: 'button' | 'submit';
}> = ({ children, onClick, primary = false, disabled = false, reason, type = 'button' }) => (
  <DosenTooltip title={disabled && reason ? reason : ''} placement="bottom">
    {/* The span takes the hover, since a disabled button gives none. */}
    <Box component="span" sx={{ display: 'inline-flex', flexShrink: 0 }}>
      <DosenButton
        type={type}
        disabled={disabled}
        onClick={onClick}
        tone={primary ? 'cta' : 'basic'}
        sizeVariant="sm"
        sx={{ whiteSpace: 'nowrap' }}
      >
        {children}
      </DosenButton>
    </Box>
  </DosenTooltip>
);

/** The mark on a choice: an empty ring until chosen, then a brandy tick. Round for one-of, square for any-of. */
const ChoiceMark: React.FC<{ on: boolean; multi?: boolean }> = ({ on, multi = false }) => (
  <Box
    aria-hidden
    sx={{
      width: 14,
      height: 14,
      flexShrink: 0,
      borderRadius: multi ? '3px' : '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: `1.5px solid ${on ? BRANDY : TEXT_4}`,
      bgcolor: on ? BRANDY : 'transparent',
      transition: 'background-color 0.15s, border-color 0.15s'
    }}
  >
    {on && <CheckIcon sx={{ fontSize: 11, color: WELL }} />}
  </Box>
);

/** A choice among a few, on one line: the role chips' shape, with a mark that fills when chosen. */
export const Chip: React.FC<{
  label: string;
  selected: boolean;
  onClick: () => void;
  multi?: boolean;
  disabled?: boolean;
  /** Why it can't be chosen. */
  reason?: string;
  /** What the choice means, on hover. */
  tip?: string;
  /** Something is being made for this choice: a spinner takes the mark's place. */
  loading?: boolean;
}> = ({ label, selected, onClick, multi = false, disabled = false, reason, tip, loading = false }) => (
  <DosenTooltip title={disabled && reason ? reason : tip ?? ''} placement="bottom">
    <Box component="span" sx={{ display: 'inline-flex' }}>
      <Box
        component="button"
        type="button"
        role={multi ? 'checkbox' : 'radio'}
        aria-checked={selected}
        disabled={disabled}
        onClick={onClick}
        sx={{
          height: 32,
          pl: '10px',
          pr: '12px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          borderRadius: '4px',
          border: selected ? BRANDY_FRAME : `1px solid ${LINE_2}`,
          bgcolor: selected ? BRANDY_FILL : 'transparent',
          color: selected ? TEXT : TEXT_2,
          fontFamily: FONT,
          fontSize: 13,
          fontWeight: selected ? 600 : 400,
          whiteSpace: 'nowrap',
          cursor: 'pointer',
          transition: 'border-color 0.15s, color 0.15s, background-color 0.15s',
          '&:hover': { color: TEXT, borderColor: BRANDY },
          '&:disabled': { opacity: 0.4, cursor: 'default', color: TEXT_2, borderColor: LINE_2 }
        }}
      >
        {loading ? (
          <CircularProgress size={14} thickness={5} sx={{ color: BRANDY, flexShrink: 0 }} aria-label="Elliott is drafting" />
        ) : (
          <ChoiceMark on={selected} multi={multi} />
        )}
        {label}
      </Box>
    </Box>
  </DosenTooltip>
);

/** A choice that needs a sentence to explain it: a title and a line beneath, in the brandy bar's frame when chosen. */
export const ChoiceCard: React.FC<{
  title: string;
  body: string;
  selected: boolean;
  onClick: () => void;
  disabled?: boolean;
}> = ({ title, body, selected, onClick, disabled = false }) => (
  <Box
    component="button"
    type="button"
    role="radio"
    aria-checked={selected}
    disabled={disabled}
    onClick={onClick}
    sx={{
      minWidth: 0,
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      p: '12px',
      textAlign: 'left',
      borderRadius: '4px',
      border: selected ? BRANDY_FRAME : `1px solid ${LINE_2}`,
      bgcolor: selected ? BRANDY_TINT : 'transparent',
      cursor: 'pointer',
      transition: 'border-color 0.15s, background-color 0.15s',
      '&:hover': { borderColor: BRANDY },
      '&:disabled': { opacity: 0.45, cursor: 'default', borderColor: LINE_2 }
    }}
  >
    <Box sx={{ pt: '2px' }}>
      <ChoiceMark on={selected} />
    </Box>
    <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <Typography sx={{ fontFamily: FONT, fontSize: 13, fontWeight: 600, lineHeight: 1.25, color: TEXT }}>
        {title}
      </Typography>
      <Typography sx={{ ...BODY, fontSize: 12, lineHeight: 1.4, color: TEXT_3, whiteSpace: 'pre-line' }}>
        {body}
      </Typography>
    </Box>
  </Box>
);

/** A two- or three-way switch on one line (By Role | By Attributes, Any | All). */
export const Segmented = <T extends string>({
  options,
  value,
  onChange,
  small = false,
  label
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  small?: boolean;
  /** What the choice is about, for assistive tech (and tests), when the line doesn't say. */
  label?: string;
}) => (
  <Box
    role="radiogroup"
    aria-label={label}
    sx={{ display: 'inline-flex', p: '2px', borderRadius: '4px', bgcolor: WELL, border: `1px solid ${LINE_2}` }}
  >
    {options.map((o) => {
      const on = o.value === value;
      return (
        <Box
          key={o.value}
          component="button"
          type="button"
          role="radio"
          aria-checked={on}
          onClick={() => onChange(o.value)}
          sx={{
            height: small ? 22 : 28,
            px: small ? '10px' : '14px',
            border: 'none',
            borderRadius: '3px',
            bgcolor: on ? BRANDY_FILL : 'transparent',
            color: on ? BRANDY : TEXT_3,
            fontFamily: FONT,
            fontSize: small ? 11 : 12.5,
            fontWeight: 600,
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            transition: 'color 0.15s, background-color 0.15s',
            '&:hover': { color: on ? BRANDY : TEXT }
          }}
        >
          {o.label}
        </Box>
      );
    })}
  </Box>
);

/** A dropdown in the input's shape: the value, an arrow, a menu of options. */
export const SelectField: React.FC<{
  label: string;
  options: { id: string; name: string }[];
  value: string | null;
  placeholder: string;
  onChange: (id: string) => void;
}> = ({ label, options, value, placeholder, onChange }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const current = options.find((o) => o.id === value);
  return (
    <>
      <Box
        component="button"
        type="button"
        aria-haspopup="listbox"
        aria-label={`${label}: ${current?.name ?? placeholder}`}
        onClick={(e: React.MouseEvent<HTMLElement>) => setAnchor(e.currentTarget)}
        sx={{
          ...INPUT_SX,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          pr: '8px',
          textAlign: 'left',
          cursor: 'pointer',
          color: current ? TEXT : TEXT_4,
          ...(anchor ? { borderColor: BRANDY } : {}),
          [MOBILE_SX]: { height: 'auto', minHeight: 40, py: '8px' }
        }}
      >
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            [MOBILE_SX]: { whiteSpace: 'normal', overflowWrap: 'anywhere' }
          }}
        >
          {current?.name ?? placeholder}
        </Box>
        <ExpandMoreIcon sx={{ fontSize: 18, color: TEXT_2 }} />
      </Box>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        PaperProps={{
          sx: {
            mt: '4px',
            minWidth: anchor?.offsetWidth,
            maxHeight: 320,
            bgcolor: '#232323',
            border: `1px solid ${LINE}`,
            borderRadius: '4px',
            boxShadow: '0 12px 32px rgba(0,0,0,0.55)'
          }
        }}
      >
        {options.map((o) => (
          <MenuItem
            key={o.id}
            selected={o.id === value}
            onClick={() => {
              onChange(o.id);
              setAnchor(null);
            }}
            sx={{
              fontFamily: FONT,
              fontSize: 13,
              color: o.id === value ? BRANDY : TEXT,
              [MOBILE_SX]: { whiteSpace: 'normal', overflowWrap: 'anywhere' }
            }}
          >
            {o.name}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

/**
 * The bar along the bottom of a step, always in view: where the step stands
 * (brandy while there is something to do, green with a tick once it is ready)
 * and its actions on the right.
 */
export const BottomBar: React.FC<{ title: string; body?: string; done?: boolean; children?: React.ReactNode }> = ({
  title,
  body,
  done = false,
  children
}) => (
  <Box sx={{ flexShrink: 0, p: '12px 16px', borderTop: `1px solid ${LINE}`, bgcolor: '#0F0F0F' }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '14px',
        p: '12px 14px',
        borderRadius: '4px',
        border: done ? `1px solid ${DONE}55` : BRANDY_FRAME,
        bgcolor: done ? `${DONE}0F` : BRANDY_TINT
      }}
    >
      {done && (
        <Box
          sx={{
            width: 32,
            height: 32,
            flexShrink: 0,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: `${DONE}22`
          }}
        >
          <CheckIcon sx={{ fontSize: 17, color: DONE }} />
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 260 }}>
        <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 600, lineHeight: 1.3, color: TEXT }}>
          {title}
        </Typography>
        {body && <Typography sx={{ ...BODY, fontSize: 12.5, lineHeight: 1.45, mt: '2px' }}>{body}</Typography>}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>{children}</Box>
    </Box>
  </Box>
);

// ---------------------------------------------------------------------------
// The creation flow's frame: a stepper, one centred column of rows, a bottom bar
// ---------------------------------------------------------------------------

/** The width of the flow's single column: the stepper, the rows and the bottom bar all share it. */
export const FLOW_WIDTH = 720;
/** The parts of a section title either side of its required mark; spaces between them are kept. */
const TITLE_UNDERLINE = { whiteSpace: 'pre' as const };

/**
 * One input of a step: its name above it (a brandy mark when required, an info
 * mark for the explanation), then the control. No box of its own: inputs stack
 * one beneath the next with a single brandy hairline between them (grey
 * hairlines are for the lines within an input).
 */
export const FormRow: React.FC<{
  label: string;
  required?: boolean;
  /**
   * The leading part of the name the required mark follows, when only that
   * part is required ("Owner" in "Owner & Timing"). Defaults to the whole name.
   */
  requiredAfter?: string;
  /** A short explanation, behind an info mark beside the name. */
  tip?: string;
  /** On the right of the name's line (a switch, a count). */
  aside?: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, required = false, requiredAfter, tip, aside, children }) => {
  const head = required && requiredAfter && label.startsWith(requiredAfter) ? requiredAfter : label;
  const tail = label.slice(head.length);
  return (
    <Box
      role="group"
      aria-label={label}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
        py: '18px',
        '&:first-of-type': { pt: '4px' },
        '&:not(:first-of-type)': { borderTop: BRANDY_FRAME }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '7px', minHeight: 22 }}>
        <Typography sx={{ ...SECTION_TITLE, fontSize: 12.5, letterSpacing: '0.04em' }}>
          <Box component="span" sx={TITLE_UNDERLINE}>
            {head}
          </Box>
          {required && (
            <Box component="span" aria-label="required" sx={{ color: BRANDY, ml: '4px' }}>
              *
            </Box>
          )}
          {tail && (
            <Box component="span" sx={TITLE_UNDERLINE}>
              {tail}
            </Box>
          )}
        </Typography>
        {tip && (
          <InfoTip title={tip} placement="bottom-start" />
        )}
        <Box sx={{ flex: 1 }} />
        {aside}
      </Box>
      {children}
    </Box>
  );
};

/**
 * A line of options. It never spills onto a second line: with more than fit,
 * it scrolls left to right on a slim bar.
 */
export const ChipLine: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      overflowX: 'auto',
      pb: '2px',
      '& > *': { flexShrink: 0 },
      scrollbarWidth: 'thin',
      scrollbarColor: `${TEXT_4} transparent`,
      '&::-webkit-scrollbar': { height: '4px' },
      '&::-webkit-scrollbar-track': { background: 'transparent' },
      '&::-webkit-scrollbar-thumb': { background: TEXT_4, borderRadius: '2px' }
    }}
  >
    {children}
  </Box>
);

/** A note the admin must not miss, in the brandy frame and fill. */
export const Note: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ p: '10px 12px', borderRadius: '4px', border: BRANDY_FRAME, bgcolor: BRANDY_FILL }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 12.5, lineHeight: 1.5, color: TEXT }}>{children}</Typography>
  </Box>
);

/** A small heading inside a row, where its content has more than one group (a role's capabilities, an attribute's values). */
export const RowGroupLabel: React.FC<{ children: React.ReactNode; aside?: React.ReactNode }> = ({
  children,
  aside
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', minHeight: 22 }}>
    <Typography sx={{ ...LABEL, flex: 1, color: TEXT_2 }}>{children}</Typography>
    {aside}
  </Box>
);

export const TEXTAREA_SX = {
  ...INPUT_SX,
  height: 'auto',
  py: '9px',
  lineHeight: 1.5,
  resize: 'vertical' as const,
  display: 'block'
};

/** The flow's stepper, in the Capability Setup stepper's design: green tick, brandy number, grey to come. */
export const Stepper: React.FC<{ steps: string[]; current: number }> = ({ steps, current }) => {
  // Only where the line scrolls (a phone): the step in hand is brought into view when it changes.
  const line = React.useRef<HTMLOListElement>(null);
  React.useLayoutEffect(() => {
    const ol = line.current;
    const step = ol?.querySelector<HTMLElement>('[aria-current="step"]');
    if (ol && step && ol.scrollWidth > ol.clientWidth) {
      ol.scrollLeft = step.offsetLeft - (ol.clientWidth - step.offsetWidth) / 2;
    }
  }, [current]);
  return (
    <Box
      component="ol"
      ref={line}
      sx={{
        m: 0,
        p: 0,
        listStyle: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'wrap',
        rowGap: '10px',
        // A phone: one line that swipes sideways, edge to edge, rather than rows of steps.
        [MOBILE_SX]: {
          position: 'relative',
          flexWrap: 'nowrap',
          justifyContent: 'flex-start',
          overflowX: 'auto',
          mx: '-16px',
          px: '16px',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' }
        }
      }}
    >
      {steps.map((label, i) => {
        const done = i < current;
        const on = i === current;
        const color = done ? DONE : on ? BRANDY : TEXT_3;
        return (
          <React.Fragment key={label}>
            {i > 0 && (
              <Box
                aria-hidden
                sx={{ flexShrink: 0, width: 28, height: '1.5px', mx: '10px', bgcolor: i <= current ? DONE : '#333333' }}
              />
            )}
            <Box
              component="li"
              aria-current={on ? 'step' : undefined}
              sx={{ display: 'flex', alignItems: 'center', gap: '9px', [MOBILE_SX]: { flexShrink: 0 } }}
            >
              <Box
                sx={{
                  width: 24,
                  height: 24,
                  flexShrink: 0,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: `1.5px solid ${color}`,
                  bgcolor: done ? DONE : on ? BRANDY_FILL : 'transparent',
                  color: done ? WELL : color,
                  fontFamily: FONT,
                  fontSize: 11.5,
                  fontWeight: 700,
                  lineHeight: 1
                }}
              >
                {done ? <CheckIcon sx={{ fontSize: 15 }} /> : i + 1}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Typography
                  sx={{
                    fontFamily: FONT,
                    fontSize: 12.5,
                    fontWeight: 600,
                    lineHeight: 1.15,
                    whiteSpace: 'nowrap',
                    color: done || on ? TEXT : TEXT_3
                  }}
                >
                  {label}
                </Typography>
                <Typography
                  sx={{ fontFamily: FONT, fontSize: 10.5, fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap', color }}
                >
                  {done ? 'Done' : on ? 'In progress' : 'To do'}
                </Typography>
              </Box>
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

/**
 * A step of the flow: the stepper in a strip on top, the step's inputs in one
 * centred column that scrolls, and the bottom bar (the same width, centred)
 * always in view.
 */
export const FlowStep: React.FC<{
  stepper: React.ReactNode;
  /** The bottom bar's status and actions. `previous` sits on the bar's left, the actions on its right. */
  bar: { title: string; body?: string; done?: boolean; actions: React.ReactNode; previous?: React.ReactNode };
  children: React.ReactNode;
}> = ({ stepper, bar, children }) => {
  const scroller = React.useRef<HTMLDivElement>(null);
  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', fontFamily: FONT }}>
      <Box
        sx={{
          flexShrink: 0,
          px: '24px',
          py: '14px',
          borderBottom: `1px solid ${LINE}`,
          [MOBILE_SX]: { px: '16px', py: '10px' }
        }}
      >
        {stepper}
      </Box>
      <Box
        ref={scroller}
        sx={{ flex: 1, minHeight: 0, px: '24px', py: '16px', ...SCROLL, [MOBILE_SX]: { px: '16px' } }}
      >
        <Box sx={{ maxWidth: FLOW_WIDTH, mx: 'auto', display: 'flex', flexDirection: 'column' }}>{children}</Box>
      </Box>
      <Box sx={{ flexShrink: 0, px: '24px', py: '12px', borderTop: `1px solid ${LINE}`, [MOBILE_SX]: { px: '16px' } }}>
        <Box
          sx={{
            maxWidth: FLOW_WIDTH,
            mx: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            p: '10px 12px 10px 14px',
            borderRadius: '8px',
            border: bar.done ? `1px solid ${DONE}55` : BRANDY_FRAME,
            bgcolor: bar.done ? `${DONE}0F` : BRANDY_TINT,
            // A phone: the status on its own row, whole; Back and the actions on the row beneath.
            [MOBILE_SX]: {
              flexWrap: 'wrap',
              rowGap: '10px',
              '& > .flow-bar-done': { order: -2 },
              '& > .flow-bar-status': { order: -1, flex: '1 1 calc(100% - 44px)' },
              '& > .flow-bar-actions': { ml: 'auto' }
            }
          }}
        >
          {bar.previous && <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{bar.previous}</Box>}
          {bar.done && (
            <Box
              className="flow-bar-done"
              sx={{
                width: 30,
                height: 30,
                flexShrink: 0,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `${DONE}22`
              }}
            >
              <CheckIcon sx={{ fontSize: 16, color: DONE }} />
            </Box>
          )}
          <Box className="flow-bar-status" sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 600, lineHeight: 1.3, color: TEXT }}>
              {bar.title}
            </Typography>
            {bar.body && (
              <Typography
                sx={{
                  ...BODY,
                  fontSize: 12.5,
                  lineHeight: 1.4,
                  mt: '1px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  [MOBILE_SX]: { whiteSpace: 'normal' }
                }}
              >
                {bar.body}
              </Typography>
            )}
          </Box>
          <Box className="flow-bar-actions" sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
            {bar.actions}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * A numbered dot, as the stepper draws its steps: a green tick when done, the
 * number in brandy when it is the one in hand, grey when it is still to come.
 */
export const StepDot: React.FC<{ number: number; state: 'done' | 'current' | 'todo' }> = ({ number, state }) => {
  const color = state === 'done' ? DONE : state === 'current' ? BRANDY : TEXT_4;
  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        flexShrink: 0,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: `1.5px solid ${color}`,
        bgcolor: state === 'done' ? DONE : state === 'current' ? BRANDY_FILL : 'transparent',
        color: state === 'done' ? WELL : color,
        fontFamily: FONT,
        fontSize: 11.5,
        fontWeight: 700,
        lineHeight: 1
      }}
    >
      {state === 'done' ? <CheckIcon sx={{ fontSize: 15 }} /> : number}
    </Box>
  );
};

/**
 * One cell of a grid of choices (capabilities): the chip's look, filling its
 * column, its label cut with an ellipsis rather than wrapping.
 */
export const GridChoice: React.FC<{
  label: string;
  selected: boolean;
  onClick: () => void;
  /** What the choice means, on hover. */
  tip?: string;
}> = ({ label, selected, onClick, tip }) => (
  <DosenTooltip title={tip ?? ''} placement="bottom" enterDelay={400}>
    <Box
      component="button"
      type="button"
      role="checkbox"
      aria-checked={selected}
      onClick={onClick}
      sx={{
        minWidth: 0,
        height: 34,
        px: '10px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        textAlign: 'left',
        borderRadius: '4px',
        border: selected ? BRANDY_FRAME : `1px solid ${LINE_2}`,
        bgcolor: selected ? BRANDY_FILL : 'transparent',
        color: selected ? TEXT : TEXT_2,
        fontFamily: FONT,
        fontSize: 13,
        fontWeight: selected ? 600 : 400,
        cursor: 'pointer',
        transition: 'border-color 0.15s, color 0.15s, background-color 0.15s',
        '&:hover': { color: TEXT, borderColor: BRANDY },
        [MOBILE_SX]: { height: 'auto', minHeight: 40, py: '8px' }
      }}
    >
      <ChoiceMark on={selected} multi />
      <Box
        component="span"
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          [MOBILE_SX]: { whiteSpace: 'normal', overflowWrap: 'anywhere' }
        }}
      >
        {label}
      </Box>
    </Box>
  </DosenTooltip>
);

/** The grid those cells sit in: even columns, rows as needed. */
export const ChoiceGrid: React.FC<{ columns?: number; children: React.ReactNode }> = ({ columns = 3, children }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
      gap: '8px',
      // A phone: one choice to a row, its whole name in view.
      [MOBILE_SX]: { gridTemplateColumns: '1fr' }
    }}
  >
    {children}
  </Box>
);

const PickerIcon: React.FC = () => <CalendarTodayOutlinedIcon sx={{ fontSize: 16, color: TEXT_2 }} />;

/**
 * A date, chosen from the Dosen calendar: the input's shape, with the dark
 * calendar and brandy for the selected day. `prefix` names the date inside
 * the field ("Publish By") where it has no title of its own.
 */
export const DateField: React.FC<{
  label: string;
  value: Date | null;
  onChange: (date: Date | null) => void;
  prefix?: string;
  width?: number | string;
}> = ({ label, value, onChange, prefix, width = '100%' }) => (
  <LocalizationProvider dateAdapter={AdapterDateFns}>
    <DesktopDatePicker
      value={value}
      onChange={(d: Date | null) => onChange(d)}
      format="MMM d, yyyy"
      slots={{ openPickerIcon: PickerIcon }}
      slotProps={{
        actionBar: { actions: ['clear'] },
        textField: {
          size: 'small',
          inputProps: { 'aria-label': label },
          InputProps: prefix
            ? {
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography sx={{ ...LABEL, color: TEXT_3 }}>{prefix}</Typography>
                  </InputAdornment>
                )
              }
            : undefined,
          sx: {
            width,
            '& .MuiOutlinedInput-root': {
              height: 36,
              bgcolor: WELL,
              borderRadius: '4px',
              color: TEXT,
              fontFamily: FONT,
              fontSize: 13,
              '& fieldset': { borderColor: LINE_2, borderWidth: '1px' },
              '&:hover fieldset': { borderColor: TEXT_3 },
              '&.Mui-focused fieldset': { borderColor: BRANDY, borderWidth: '1px' }
            },
            '& .MuiOutlinedInput-input': {
              py: 0,
              pl: prefix ? 0 : '12px',
              '&::placeholder': { color: TEXT_4, opacity: 1 }
            },
            '& .MuiIconButton-root': { mr: '-6px' }
          }
        },
        popper: {
          sx: {
            '& .MuiPaper-root': {
              bgcolor: '#151515',
              border: '1px solid #444444',
              borderRadius: '4px',
              boxShadow: '0px 0px 15px rgba(255, 255, 255, 0.12)',
              p: '8px'
            },
            '& .MuiPickersCalendarHeader-label': { color: TEXT, fontFamily: FONT, fontSize: 16, fontWeight: 400 },
            '& .MuiPickersCalendarHeader-switchViewButton': { color: TEXT },
            '& .MuiPickersArrowSwitcher-button': {
              width: 28,
              height: 28,
              bgcolor: TEXT_4,
              borderRadius: '4px',
              color: TEXT,
              '& .MuiSvgIcon-root': { fontSize: 16, color: TEXT }
            },
            '& .MuiDayCalendar-weekDayLabel': { color: TEXT_2, fontFamily: FONT, fontSize: 12, fontWeight: 500 },
            '& .MuiPickersDay-root': {
              color: TEXT,
              bgcolor: 'transparent',
              fontFamily: FONT,
              fontSize: 12,
              fontWeight: 500,
              borderRadius: '999px',
              '&:hover': { bgcolor: LINE },
              '&.MuiPickersDay-today': { borderColor: TEXT_3 },
              '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus': {
                background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
                color: TEXT
              },
              '&.MuiPickersDay-dayOutsideMonth': { color: TEXT_3 }
            },
            '& .MuiPickersYear-yearButton': { color: TEXT, fontFamily: FONT },
            '& .MuiPickersYear-yearButton.Mui-selected': {
              background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
              color: TEXT
            },
            '& .MuiDialogActions-root .MuiButton-root': { color: BRANDY, fontFamily: FONT, textTransform: 'none' }
          }
        }
      }}
    />
  </LocalizationProvider>
);
